/**
 * Detect image format từ file content (magic number)
 * @param file - File object từ input
 * @returns Promise<string | null> - Extension đúng (jpg, png, gif, webp) hoặc null nếu không phải ảnh
 */
export const detectImageFormat = async (file: File): Promise<string | null> => {
  return new Promise((resolve) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const arrayBuffer = e.target?.result as ArrayBuffer;
      const bytes = new Uint8Array(arrayBuffer);

      // Cần ít nhất 4 bytes để detect
      if (bytes.length < 4) {
        resolve(null);
        return;
      }

      // JPEG: FF D8 FF
      if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
        resolve('jpg');
        return;
      }

      // PNG: 89 50 4E 47
      if (
        bytes[0] === 0x89 &&
        bytes[1] === 0x50 &&
        bytes[2] === 0x4e &&
        bytes[3] === 0x47
      ) {
        resolve('png');
        return;
      }

      // GIF: 47 49 46 38 (GIF89a hoặc GIF87a)
      if (
        bytes[0] === 0x47 &&
        bytes[1] === 0x49 &&
        bytes[2] === 0x46 &&
        bytes[3] === 0x38
      ) {
        resolve('gif');
        return;
      }

      // WebP: RIFF...WEBP
      if (bytes.length >= 12) {
        const header = String.fromCharCode(...Array.from(bytes.slice(0, 4)));
        const webpHeader = String.fromCharCode(...Array.from(bytes.slice(8, 12)));
        if (header === 'RIFF' && webpHeader === 'WEBP') {
          resolve('webp');
          return;
        }
      }

      // Không phải ảnh
      resolve(null);
    };

    reader.onerror = () => {
      resolve(null);
    };

    // Chỉ đọc 12 bytes đầu (đủ để detect format)
    const blob = file.slice(0, 12);
    reader.readAsArrayBuffer(blob);
  });
};

/**
 * Validate và fix file name với extension đúng
 * @param file - File object
 * @returns Promise<{ isValid: boolean; fixedName: string; error?: string }>
 */
export const validateAndFixImageFile = async (
  file: File
): Promise<{ isValid: boolean; fixedName: string; error?: string }> => {
  // Detect format từ content
  const detectedFormat = await detectImageFormat(file);

  // Nếu không phải ảnh → Reject
  if (!detectedFormat) {
    const ext = file.name.split('.').pop()?.toLowerCase() || '';

    // Detect loại file không phải ảnh để có error message rõ ràng
    let fileType = 'unknown';
    if (ext === 'zip' || ext === 'rar' || ext === '7z') {
      fileType = '압축 파일 (ZIP)';
    } else if (ext === 'pdf') {
      fileType = 'PDF 파일';
    } else if (ext === 'doc' || ext === 'docx') {
      fileType = '문서 파일 (Word)';
    } else if (ext === 'xls' || ext === 'xlsx') {
      fileType = '엑셀 파일';
    } else {
      fileType = '알 수 없는 파일';
    }

    return {
      isValid: false,
      fixedName: file.name,
      error: `${fileType}은 업로드할 수 없습니다. 이미지 파일만 업로드 가능합니다. (지원 형식: jpg, jpeg, png, gif, webp)`,
    };
  }

  // File là ảnh → Fix extension nếu cần
  const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
  const currentExt = file.name.split('.').pop()?.toLowerCase() || '';

  // Nếu extension đã đúng → Không cần fix
  if (
    currentExt === detectedFormat ||
    (detectedFormat === 'jpg' && (currentExt === 'jpeg' || currentExt === 'jpg'))
  ) {
    return {
      isValid: true,
      fixedName: file.name,
    };
  }

  // Fix extension
  const correctExt = detectedFormat === 'jpg' ? 'jpg' : detectedFormat;
  const fixedName = `${nameWithoutExt}.${correctExt}`;

  return {
    isValid: true,
    fixedName: fixedName,
  };
};
